/**
 * P1-IDENTITY-PROPAGATION — Bridge between the canonical nation
 * (`profiles.selection_team`, read via `useSelectedNation`) and the
 * client-side Team Theme Engine (PR-35D).
 *
 * When the user picks / changes their nation in `NationGate`, this listener
 * mirrors the ISO-3 code into the team palette so the chosen colors,
 * gradient and accents propagate globally (Home, Header, Match Hub,
 * Nation Wars, IdentityHub, Commercial overlays) — without touching
 * scoring, DB, or NationGate logic.
 *
 * On bootstrap, if the signed-in user already has `selection_team`,
 * the palette is synced once so first-render identity matches the
 * server-persisted nation (not the previous device's localStorage).
 */
import { supabase } from "@/integrations/supabase/client";
import {
  TEAM_PALETTES,
  setFavoriteTeam,
  getActiveThemeId,
  type TeamId,
} from "@/lib/pr35d/teamThemeEngine";

const CHANGE_EVENT = "vs:nation-changed";

function knownTeamId(code: string | null | undefined): TeamId | null {
  if (!code) return null;
  const upper = code.toUpperCase();
  return TEAM_PALETTES.find((p) => p.id === upper)?.id ?? null;
}

async function syncFromProfile(): Promise<void> {
  try {
    const { data: auth } = await supabase.auth.getUser();
    const uid = auth.user?.id;
    if (!uid) return;
    const { data } = await supabase
      .from("profiles")
      .select("selection_team")
      .eq("id", uid)
      .maybeSingle();
    const code = (data?.selection_team as string | null) ?? null;
    const id = knownTeamId(code);
    if (id && getActiveThemeId() !== id) {
      setFavoriteTeam(id);
    }
  } catch {
    /* noop — identity propagation must never crash the app */
  }
}

let bootstrapped = false;

export function bootstrapNationThemeBridge(): void {
  if (bootstrapped || typeof window === "undefined") return;
  bootstrapped = true;
  window.addEventListener(CHANGE_EVENT, () => { void syncFromProfile(); });
  // Initial sync once (handles refresh after sign-in on a new device).
  void syncFromProfile();
}